import { ShieldAlert } from 'lucide-react';
import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';

/**
 * Warns a reader that the document they have opened is CONFIDENTIAL.
 *
 * **It writes nothing, so it carries no confirm tick** — the confirm rule gates the primary
 * action of a transaction, and this has no action at all. A tick in front of "you have opened a
 * confidential document" is ceremony that teaches readers to click past ticks, which is the
 * opposite of what a warning is for. `<ImagePreviewModal>` makes the same call for the same
 * reason.
 *
 * **It does not gate anything, and must not be mistaken for a control.** The reader already
 * holds `repository.read` on this branch — the server decided that before the page rendered.
 * What this adds is that they know what they are looking at before they read it, forward it, or
 * leave it on a screen. Treating it as protection would be treating a notice as a permission.
 *
 * **Which documents it fires for is the CATALOG's answer, not this component's.** It reads
 * `classificationIsConfidential`, which is resolved live through the classification join — so an
 * administrator marking a classification confidential starts warning on every document already
 * filed under it, with nothing here to change.
 */
export function ConfidentialWarningModal({
  open,
  classificationName,
  documentName,
  onAcknowledge,
}: {
  open: boolean;
  /** The classification's own name — the catalog's word, never one coined here. */
  classificationName: string | null;
  documentName: string;
  onAcknowledge: () => void;
}) {
  return (
    <Modal
      open={open}
      onClose={onAcknowledge}
      title="Confidential document"
      size="md"
      id="repository-confidential-warning"
      footer={
        <Button
          id="repository-confidential-acknowledge"
          variant="primary"
          onClick={onAcknowledge}
        >
          I understand
        </Button>
      }
    >
      <div className="flex gap-3">
        <span className="mt-0.5 shrink-0 text-red-600">
          <ShieldAlert className="h-5 w-5" aria-hidden="true" />
        </span>

        <div className="flex flex-col gap-2 text-sm text-slate-700">
          <p>
            <span className="font-medium text-slate-900">{documentName}</span> is classified{' '}
            <span className="font-medium text-slate-900">
              {classificationName ?? 'confidential'}
            </span>
            .
          </p>

          {/*
            What the reader should actually DO, rather than a restatement of the pill they can
            already see. A warning that only says "this is confidential" tells them nothing they
            did not learn from the red pill three inches above it.
          */}
          <p>
            Take care where you read it and who can see your screen. Do not forward it, print it,
            or copy its contents to anyone outside the people it was shared with.
          </p>

          {/*
            There is deliberately NO line here saying the access is recorded against the reader.
            It would be false: the HTTP audit filter skips GETs, so opening, previewing and
            downloading a document write no audit row. Claiming otherwise would be a deterrent
            built on a fact the system does not have — and the reader would find out it was
            untrue the first time they looked for the trail.
          */}
        </div>
      </div>
    </Modal>
  );
}
